import { useHandler } from "@redotech/react-util/hook";
import GitBranchIcon from "@redotech/redo-icons/git-branch-01.svg";
import SwitchHorizontalIcon from "@redotech/redo-icons/switch-horizontal-01_filled.svg";
import { RcoSegmentStep } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  narySegmentCharacteristics,
  segmentableCharacteristicLabel,
} from "@redotech/redo-model/checkouts/segments";
import { AutomationForgeElementType } from "@redotech/redo-model/forge/marketing-automation-forge-types";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoCommandMenuItem } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { isDef } from "@redotech/util/checks";
import { Handle, Position } from "@xyflow/react";
import { memo, useMemo } from "react";
import { FlowEdge, NodeTypes, TypedFlowNode } from "../flow-converter";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as css from "./node.module.css";

export interface RcoSegmentNodeData {
  step: PartialWithRequiredType<RcoSegmentStep>;
  deleteElements: (id: string) => void;
  updateStep: (
    id: string,
    updatedStep: PartialWithRequiredType<RcoSegmentStep>,
  ) => void;
  readonly: boolean;
}

export function isNarySegmentStep(
  step: PartialWithRequiredType<RcoSegmentStep>,
): boolean {
  const characteristic = step.segment?.segmentableCharacteristic;
  return (
    isDef(characteristic) && narySegmentCharacteristics.has(characteristic)
  );
}

export const RcoSegmentNode = memo(function RcoSegmentNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: RcoSegmentNodeData;
  selected?: boolean | undefined;
}) {
  const isNary = isNarySegmentStep(data.step);

  const handleSwapEdges = useHandler(() => {
    const swappedStep = {
      ...data.step,
      matchingNextId: data.step.nonMatchingNextId,
      nonMatchingNextId: data.step.matchingNextId,
    };
    data.updateStep(id, swappedStep);
  });

  const additionalMenuItems: RedoCommandMenuItem[] = useMemo(() => {
    if (data.readonly || isNary) {
      return [];
    }

    return [
      {
        text: "Swap edges",
        onClick: handleSwapEdges,
        Icon: SwitchHorizontalIcon,
        theme: "normal" as const,
      },
    ];
  }, [data.readonly, isNary, handleSwapEdges]);

  return (
    <BaseNode
      additionalMenuItems={additionalMenuItems}
      containerClassName={css.checkoutNode}
      icon={<RedoFeaturedIcon color="success" Icon={GitBranchIcon} size="sm" />}
      onDelete={data.readonly ? undefined : () => data.deleteElements(id)}
      selected={selected}
      step={data.step}
      title="Segment"
    >
      <Handle id="top" position={Position.Top} type="target" />
      <Flex align="flex-start" dir="column" gap="xs">
        <SegmentDetails step={data.step} />
      </Flex>
      <Handle id="bottom" position={Position.Bottom} type="source" />
    </BaseNode>
  );
});

const SegmentDetails = memo(function SegmentDetails({
  step,
}: {
  step: PartialWithRequiredType<RcoSegmentStep>;
}) {
  return step.segment?.segmentableCharacteristic ? (
    <>
      <Text fontSize="xxs" textColor="secondary">
        Property
      </Text>
      <Flex w="full">
        <RedoBadge
          color="gray"
          size="xs"
          text={
            segmentableCharacteristicLabel[
              step.segment.segmentableCharacteristic
            ]
          }
        />
      </Flex>
    </>
  ) : null;
});

export function getRcoSegmentEdgesAndDropZones({
  stepId,
  matchingNextId,
  nonMatchingNextId,
  elementDropped,
  readonly,
}: {
  stepId: string;
  matchingNextId: string | undefined;
  nonMatchingNextId: string | undefined;
  elementDropped: (
    elementType: AutomationForgeElementType,
    idFrom: string,
    idTo?: string,
    conditionSide?: boolean,
  ) => void;
  readonly: boolean;
}): { edges: FlowEdge[]; dropZoneNodes: TypedFlowNode[] } {
  const edges: FlowEdge[] = [];
  const dropZoneNodes: TypedFlowNode[] = [];

  if (isDef(matchingNextId)) {
    // Don't allow dropping elements
    edges.push({
      id: stepId + "_matching",
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      targetHandle: "top",
      target: matchingNextId,
      data: {
        label: "Matches",
        color: "gray",
        ...(!readonly ? { elementDropped } : {}),
        idFrom: stepId,
        idTo: matchingNextId,
        conditionSide: true,
      },
    });
  } else if (!readonly) {
    // No matching next element, so we can allow dropping
    const dropZoneId = stepId + "_matching";
    dropZoneNodes.push({
      id: dropZoneId,
      type: NodeTypes.DROP_ZONE,
      position: { x: 0, y: 0 },
      data: {
        idFrom: stepId,
        idTo: undefined,
        conditionSide: true,
        elementDropped,
      },
    });
    edges.push({
      id: stepId + "_matching_edge",
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      target: dropZoneId,
      data: { label: "Matches", color: "gray" },
    });
  }

  if (isDef(nonMatchingNextId)) {
    // Don't allow dropping elements
    edges.push({
      id: stepId + "_non_matching",
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      targetHandle: "top",
      target: nonMatchingNextId,
      data: {
        label: "Does not match",
        color: "gray",
        idFrom: stepId,
        idTo: nonMatchingNextId,
        ...(!readonly ? { elementDropped } : {}),
        conditionSide: false,
      },
    });
  } else if (!readonly) {
    // No non-matching next element, so we can allow dropping
    const dropZoneId = stepId + "_non_matching";
    dropZoneNodes.push({
      id: dropZoneId,
      type: NodeTypes.DROP_ZONE,
      position: { x: 0, y: 0 },
      data: {
        idFrom: stepId,
        idTo: undefined,
        conditionSide: false,
        elementDropped,
      },
    });
    edges.push({
      id: stepId + "_non_matching_edge",
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      target: dropZoneId,
      data: { label: "Does not match", color: "gray" },
    });
  }

  return { edges, dropZoneNodes };
}

function getNaryEdgeLabel(index: number): string {
  return `Condition ${index + 1}`;
}

export function getRcoNarySegmentEdgesAndDropZones({
  stepId,
  stepEdges,
  defaultEdgeId,
  elementDropped,
  readonly,
}: {
  stepId: string;
  stepEdges: { edgeId?: string; nextId?: string }[] | undefined;
  defaultEdgeId: string | undefined;
  elementDropped: (
    elementType: AutomationForgeElementType,
    idFrom: string,
    idTo?: string,
    conditionSide?: boolean,
    edgeId?: string,
  ) => void;
  readonly: boolean;
}): { edges: FlowEdge[]; dropZoneNodes: TypedFlowNode[] } {
  const edges: FlowEdge[] = [];
  const dropZoneNodes: TypedFlowNode[] = [];

  // Create edges for each non-default edge
  const conditionEdges = (stepEdges ?? []).filter(
    (e) => e.edgeId !== defaultEdgeId,
  );
  for (const [index, edge] of conditionEdges.entries()) {
    const edgeId = edge.edgeId;
    const nextId = edge.nextId;
    const label = getNaryEdgeLabel(index);

    if (isDef(nextId) && nextId !== "") {
      edges.push({
        id: `${stepId}_${edgeId}`,
        type: "labeled",
        source: stepId,
        sourceHandle: "bottom",
        targetHandle: "top",
        target: nextId,
        data: {
          label,
          color: "gray",
          ...(!readonly ? { elementDropped } : {}),
          idFrom: stepId,
          idTo: nextId,
          edgeId,
        },
      });
    } else if (!readonly && edgeId) {
      const dropZoneId = `${stepId}_${edgeId}`;
      dropZoneNodes.push({
        id: dropZoneId,
        type: NodeTypes.DROP_ZONE,
        position: { x: 0, y: 0 },
        data: { idFrom: stepId, edgeId, elementDropped },
      });
      edges.push({
        id: `${stepId}_${edgeId}_edge`,
        type: "labeled",
        source: stepId,
        sourceHandle: "bottom",
        target: dropZoneId,
        data: { label, color: "gray" },
      });
    }
  }

  // Create edge for default
  const defaultNextId = stepEdges?.find(
    (e) => e.edgeId === defaultEdgeId,
  )?.nextId;
  if (isDef(defaultNextId) && defaultNextId !== "") {
    edges.push({
      id: `${stepId}_default`,
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      targetHandle: "top",
      target: defaultNextId,
      data: {
        label: "Default",
        color: "gray",
        ...(!readonly ? { elementDropped } : {}),
        idFrom: stepId,
        idTo: defaultNextId,
        edgeId: defaultEdgeId,
      },
    });
  } else if (!readonly && isDef(defaultEdgeId)) {
    const dropZoneId = `${stepId}_default`;
    dropZoneNodes.push({
      id: dropZoneId,
      type: NodeTypes.DROP_ZONE,
      position: { x: 0, y: 0 },
      data: { idFrom: stepId, edgeId: defaultEdgeId, elementDropped },
    });
    edges.push({
      id: `${stepId}_default_edge`,
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      target: dropZoneId,
      data: { label: "Default", color: "gray" },
    });
  }

  return { edges, dropZoneNodes };
}
